import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Contact, ContactService } from '../contacts/contact.service';
import { LifecycleStage, LifecycleService } from '../leads/lifecycle.service';
import { Lead, LeadService } from '../leads/lead.service';
import { AuthContext } from '../../../context/authcontext';

interface PipelineFormData {
  contacts: Contact[];
  lifecycleStages: LifecycleStage[];
  leads: Lead[];
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  getFilteredContacts: (companyId?: number | null) => Contact[];
}

const PipelineFormDataContext = createContext<PipelineFormData | undefined>(undefined);

interface PipelineFormDataProviderProps {
  children: ReactNode;
  isMainApiCompleted?: boolean;
  shouldLoad?: boolean;
}

export const PipelineFormDataProvider: React.FC<PipelineFormDataProviderProps> = ({ children, isMainApiCompleted = false, shouldLoad = true }) => {
  const { selectedTenantId } = useContext(AuthContext);

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [lifecycleStages, setLifecycleStages] = useState<LifecycleStage[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const fetchData = async () => {
    if (loading || !selectedTenantId || !isMainApiCompleted || !shouldLoad) return;

    setLoading(true);
    setError(null);
    
    try {
      // Get lifecycle types first to find PIPELINE type
      const lifecycleTypes = await LifecycleService.getLifecycleTypes(parseInt(selectedTenantId));
      const pipelineLifecycleType = lifecycleTypes.find(type => type.lifecycle_type_key === 'PIPELINE');
      
      let pipelineStages: LifecycleStage[] = [];
      if (pipelineLifecycleType) {
        pipelineStages = await LifecycleService.getLifecycleStages(
          pipelineLifecycleType.lifecycle_type_id,
          parseInt(selectedTenantId)
        );
      }

      // Fetch all reference data in parallel
      const [contactsData, leadsData] = await Promise.all([
        ContactService.getAllContacts(),
        LeadService.getAllLeads()
      ]);

      setContacts(contactsData);
      setLeads(leadsData);
      setLifecycleStages(pipelineStages);
      setDataLoaded(true);
    } catch (err) {
      console.error('Error fetching pipeline form data:', err);
      setError('Failed to load form data');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setDataLoaded(false);
    await fetchData();
  };

  const getFilteredContacts = (companyId?: number | null): Contact[] => {
    if (!companyId) {
      return contacts;
    }
    return contacts.filter(contact => contact.company_id === companyId);
  };

  // Fetch data when tenant changes or on first load - only run when main API has completed and the data is actually needed
  useEffect(() => {
    if (selectedTenantId && isMainApiCompleted && shouldLoad && !dataLoaded && !loading) {
      fetchData();
    }
  }, [selectedTenantId, isMainApiCompleted, shouldLoad, dataLoaded, loading]);

  const value: PipelineFormData = {
    contacts,
    lifecycleStages,
    leads,
    loading,
    error,
    refreshData,
    getFilteredContacts
  };

  return (
    <PipelineFormDataContext.Provider value={value}>
      {children}
    </PipelineFormDataContext.Provider>
  );
};

export const usePipelineFormData = (): PipelineFormData => {
  const context = useContext(PipelineFormDataContext);
  if (!context) {
    throw new Error('usePipelineFormData must be used within a PipelineFormDataProvider');
  }
  return context;
};
